import React from 'react';
import './Button.css';

type ButtonBaseProps = {
	variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
	size?: 'sm' | 'md' | 'lg';
	loading?: boolean;
	icon?: React.ReactNode;
	iconPosition?: 'left' | 'right';
	fullWidth?: boolean;
};

type ButtonAsButton = ButtonBaseProps &
	Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
		as?: 'button';
	};

type ButtonAsAnchor = ButtonBaseProps &
	Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
		as: 'a';
	};

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

/**
 * Reusable Button component
 * Can render as button or anchor element
 */
export const Button: React.FC<ButtonProps> = (props) => {
	const {
		children,
		variant = 'primary',
		size = 'md',
		loading = false,
		icon,
		iconPosition = 'left',
		fullWidth = false,
		className = '',
		as = 'button',
		...rest
	} = props;

	const classes = [
		'ui-btn',
		`ui-btn--${variant}`,
		`ui-btn--${size}`,
		fullWidth && 'ui-btn--full',
		loading && 'ui-btn--loading',
		className,
	].filter(Boolean).join(' ');

	const content = (
		<>
			{loading && <span className="ui-btn__spinner" />}
			{!loading && icon && iconPosition === 'left' && (
				<span className="ui-btn__icon">{icon}</span>
			)}
			<span className="ui-btn__text">{children}</span>
			{!loading && icon && iconPosition === 'right' && (
				<span className="ui-btn__icon">{icon}</span>
			)}
		</>
	);

	if (as === 'a') {
		const anchorProps = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;
		return (
			<a className={classes} {...anchorProps}>
				{content}
			</a>
		);
	}

	const buttonProps = rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
	return (
		<button
			className={classes}
			disabled={buttonProps.disabled || loading}
			{...buttonProps}
		>
			{content}
		</button>
	);
};

export default Button;
